import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../utils/apiClient';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Building2, Calendar, Eye, CheckCircle2, Loader2, Target, Pencil, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const BusinessList = () => {
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState([]);
  const [businessStats, setBusinessStats] = useState({});
  const [activeBusiness, setActiveBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingStats, setLoadingStats] = useState({});
  const [changingActive, setChangingActive] = useState(null);

  useEffect(() => {
    loadBusinesses();
    loadActiveBusiness();
  }, []);

  const loadBusinesses = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/businesses');
      setBusinesses(response.data);
      
      // Load stats for each business
      response.data.forEach(business => {
        loadBusinessStats(business.id);
      });
    } catch (error) {
      console.error('Error loading businesses:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadActiveBusiness = async () => {
    try {
      const response = await apiClient.get('/user/active-business');
      setActiveBusiness(response.data.business);
    } catch (error) {
      console.error('Error loading active business:', error);
    }
  };

  const loadBusinessStats = async (businessId) => {
    try {
      setLoadingStats(prev => ({ ...prev, [businessId]: true }));
      const response = await apiClient.get(`/businesses/${businessId}/content-summary`);
      setBusinessStats(prev => ({ ...prev, [businessId]: response.data }));
    } catch (error) {
      console.error(`Error loading stats for business ${businessId}:`, error);
    } finally {
      setLoadingStats(prev => ({ ...prev, [businessId]: false }));
    }
  };

  const handleSetActive = async (businessId, e) => {
    e.stopPropagation();
    try {
      setChangingActive(businessId);
      await apiClient.put(`/businesses/${businessId}/set-active`);
      setActiveBusiness(businesses.find(b => b.id === businessId));
      toast.success('Empresa activa actualizada');
    } catch (error) {
      console.error('Error setting active business:', error);
      toast.error('Error al cambiar empresa activa');
    } finally {
      setChangingActive(null);
    }
  };

  const handleViewContent = (businessId, e) => {
    e.stopPropagation();
    navigate(`/business-content/${businessId}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Page Header */}
      <div className="bg-white/80 border-b border-slate-200 px-4 sm:px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => window.location.href = '/dashboard'}
              className="p-2 hover:bg-slate-100 rounded-full transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-slate-600" />
            </button>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Negocios Registrados</h1>
              <p className="text-sm text-slate-600 mt-1">Historial de negocios analizados</p>
            </div>
          </div>
          <Button
            data-testid="create-new-btn"
            onClick={() => navigate('/icp', { state: { createNew: true } })}
            className="rounded-full bg-gradient-to-r from-blue-600 to-purple-600"
          >
            Crear Nuevo
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-12">
        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-slate-600">Cargando negocios...</p>
          </div>
        ) : businesses.length === 0 ? (
          <div className="text-center py-20">
            <Building2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">No hay negocios registrados</h3>
            <p className="text-slate-600 mb-6">Comienza creando tu primer ICP</p>
            <Button
              data-testid="create-first-btn"
              onClick={() => navigate('/icp')}
              className="rounded-full"
            >
              Crear Primer Negocio
            </Button>
          </div>
        ) : (
          <div>
            <div className="mb-6 p-4 bg-purple-50 rounded-lg border border-purple-200">
              <p className="text-sm text-purple-900">
                <strong>💡 Gestiona tus empresas:</strong> Tienes {businesses.length}/10 empresas creadas. 
                Puedes cambiar la empresa activa y ver todo el contenido generado para cada una.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-6">
              {businesses.map((business) => {
                const isActive = activeBusiness?.id === business.id;
                const stats = businessStats[business.id];
                const statsLoading = loadingStats[business.id];

                return (
                  <Card
                    key={business.id}
                    data-testid={`business-card-${business.id}`}
                    className={`transition-all hover:shadow-xl ${
                      isActive ? 'border-2 border-purple-400 bg-purple-50' : 'border border-slate-200 bg-white'
                    }`}
                  >
                    <CardHeader>
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                            isActive 
                              ? 'bg-gradient-to-br from-purple-500 to-pink-500' 
                              : 'bg-gradient-to-br from-blue-500 to-cyan-500'
                          }`}>
                            <Building2 className="w-6 h-6 text-white" />
                          </div>
                          {isActive && (
                            <div className="px-2 py-1 bg-purple-600 text-white text-xs font-bold rounded-full flex items-center gap-1">
                              <CheckCircle2 className="w-3 h-3" />
                              ACTIVA
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          <Calendar className="w-3 h-3" />
                          {new Date(business.created_at).toLocaleDateString()}
                        </div>
                      </div>
                      <CardTitle className="text-lg line-clamp-1">
                        {business.business_name || business.business_description.substring(0, 40) + '...'}
                      </CardTitle>
                      <CardDescription className="line-clamp-1 mt-1">{business.product_service}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {/* Business Info */}
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-slate-600">Precio:</span>
                            <span className="font-semibold">{business.price}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-600">Target:</span>
                            <span className="font-semibold text-xs">{business.target_audience}</span>
                          </div>
                        </div>

                        {/* Statistics */}
                        <div className={`p-3 rounded-lg ${isActive ? 'bg-white border border-purple-200' : 'bg-slate-50 border border-slate-200'}`}>
                          <div className="flex items-center gap-2 mb-2">
                            <Target className="w-4 h-4 text-slate-600" />
                            <h4 className="text-xs font-semibold text-slate-700">Contenido Generado</h4>
                          </div>
                          
                          {statsLoading ? (
                            <div className="text-center py-2">
                              <Loader2 className="w-4 h-4 animate-spin text-slate-400 mx-auto" />
                            </div>
                          ) : stats ? (
                            <div className="grid grid-cols-4 gap-2 text-center">
                              <div>
                                <p className="text-lg font-bold text-purple-600">{stats.statistics.campaigns}</p>
                                <p className="text-xs text-slate-600">Camp.</p>
                              </div>
                              <div>
                                <p className="text-lg font-bold text-blue-600">{stats.statistics.ads}</p>
                                <p className="text-xs text-slate-600">Ads</p>
                              </div>
                              <div>
                                <p className="text-lg font-bold text-orange-600">{stats.statistics.scripts}</p>
                                <p className="text-xs text-slate-600">Scripts</p>
                              </div>
                              <div>
                                <p className="text-lg font-bold text-pink-600">{stats.statistics.monica_projects}</p>
                                <p className="text-xs text-slate-600">Mónica</p>
                              </div>
                            </div>
                          ) : (
                            <p className="text-xs text-slate-500 text-center py-2">Sin estadísticas</p>
                          )}
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-2">
                          <Button
                            data-testid={`edit-business-${business.id}-btn`}
                            variant="outline"
                            size="sm"
                            className="flex-1 text-xs"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate('/icp', { state: { editBusiness: business } });
                            }}
                          >
                            <Pencil className="w-3 h-3 mr-1" />
                            Editar
                          </Button>
                          <Button
                            data-testid={`view-content-${business.id}-btn`}
                            variant="outline"
                            size="sm"
                            className="flex-1 text-xs"
                            onClick={(e) => handleViewContent(business.id, e)}
                          >
                            <Eye className="w-3 h-3 mr-1" />
                            Ver Contenido
                          </Button>
                          
                          {!isActive && (
                            <Button
                              data-testid={`set-active-${business.id}-btn`}
                              size="sm"
                              className="flex-1 text-xs"
                              onClick={(e) => handleSetActive(business.id, e)}
                              disabled={changingActive === business.id}
                            >
                              {changingActive === business.id ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <>
                                  <CheckCircle2 className="w-3 h-3 mr-1" />
                                  Activar
                                </>
                              )}
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BusinessList;